import React, { useState, useCallback } from 'react';
import { Fingerprint, Loader2 } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from './AuthProvider';
import axios from 'axios';
import { passkeysApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

// Dark geometric gradient that conveys structure, strategy, and precision.
// Tenants can override this via their branding settings (loginBackgroundUrl).
const defaultBackground = `
  radial-gradient(ellipse at 20% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
  radial-gradient(ellipse at 80% 20%, rgba(37, 99, 235, 0.12) 0%, transparent 50%),
  radial-gradient(ellipse at 60% 80%, rgba(13, 148, 136, 0.08) 0%, transparent 50%),
  linear-gradient(180deg, #060912 0%, #0A0E17 50%, #0D1220 100%)
`;

const gridOverlay = `
  linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
  linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
`;

interface SsoCheckResult {
  ssoEnabled: boolean;
  provider?: string;
  displayName?: string;
  loginUrl?: string;
  enforceSso?: boolean;
  error?: string;
}

// Shared input class — dark glassmorphic field with the same look the MUI version had.
const FIELD_CLASS =
  'h-12 rounded-lg border-white/10 bg-white/[0.03] text-[#E8ECF4] placeholder:text-[rgba(136,146,168,0.6)] focus-visible:ring-1 focus-visible:ring-violet-500';

const PRIMARY_GRADIENT =
  'border-0 bg-gradient-to-br from-[#7C3AED] to-[#6C3ACD] text-white shadow-[0_4px_14px_rgba(124,58,237,0.3)] hover:from-[#6D28D9] hover:to-[#5B21B6]';

const SSO_GRADIENT =
  'border-0 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white shadow-[0_4px_14px_rgba(37,99,235,0.3)] hover:from-[#1D4ED8] hover:to-[#1E40AF]';

const OAUTH_BUTTON =
  'h-11 w-full rounded-lg border border-white/10 bg-transparent text-[#E8ECF4] normal-case hover:border-white/25 hover:bg-white/[0.04]';

const PASSKEY_BUTTON =
  'h-11 w-full rounded-lg border border-violet-500/30 bg-transparent text-[#B794F4] normal-case hover:border-violet-500 hover:bg-violet-500/[0.08]';

function Divider({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-2">
      <div className="h-px flex-1 bg-white/[0.08]" />
      <span className="text-xs text-[rgba(136,146,168,0.6)]">{label}</span>
      <div className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

export default function LoginPage() {
  const { login, verifyMfa, requiresMfa, applyTokens } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [ssoChecked, setSsoChecked] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [ssoResult, setSsoResult] = useState<SsoCheckResult | null>(null);

  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const checkSso = useCallback(async (emailValue: string) => {
    if (!emailValue || !emailValue.includes('@')) return;
    setSsoChecking(true);
    try {
      const { data } = await axios.get<SsoCheckResult>(
        `${API_URL}/sso/login?email=${encodeURIComponent(emailValue)}`,
      );
      setSsoResult(data);
      setSsoChecked(true);
    } catch {
      setSsoResult(null);
      setSsoChecked(true);
    } finally {
      setSsoChecking(false);
    }
  }, []);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    if (!ssoChecked) {
      await checkSso(email);
      return;
    }

    if (ssoResult?.ssoEnabled && ssoResult.enforceSso && ssoResult.loginUrl) {
      window.location.href = ssoResult.loginUrl;
      return;
    }

    if (!password) return;
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaCode);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (ssoChecked) {
      setSsoChecked(false);
      setSsoResult(null);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetEmail = magicLinkEmail || email;
    if (!targetEmail) return;
    setError('');
    setMagicLinkLoading(true);
    try {
      await axios.post(`${API_URL}/auth/magic-link`, { email: targetEmail });
      setMagicLinkSent(true);
    } catch {
      // API always returns 200 to prevent enumeration; only network errors land here.
      setError('Unable to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const options = await passkeysApi.loginOptions(email || undefined);
      const challengeKey = options.challenge;
      const assertion = await startAuthentication({ optionsJSON: options });
      const result = await passkeysApi.login({ response: assertion, challengeKey });

      // Apply tokens through React state so OAuthAuthorizePage keeps its query
      // string. Writing sessionStorage + window.location.reload() was unreliable
      // on the authorize flow — some browsers dropped the search params.
      applyTokens(result);
      if (!window.location.pathname.startsWith('/oauth/authorize')
          && window.location.pathname !== '/') {
        window.history.replaceState(null, '', '/');
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError(err.response?.data?.error || 'Passkey authentication failed. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const showPasswordField = ssoChecked && !(ssoResult?.ssoEnabled && ssoResult.enforceSso);
  const showSsoButton = ssoChecked && ssoResult?.ssoEnabled;

  return (
    <div
      className="dark relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ background: defaultBackground }}
    >
      {/* Grid pattern overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: gridOverlay, backgroundSize: '60px 60px' }}
      />

      {/* Floating accent orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute h-[400px] w-[400px] rounded-full"
        style={{
          top: -120,
          right: -80,
          background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute h-[350px] w-[350px] rounded-full"
        style={{
          bottom: -100,
          left: -60,
          background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Login card */}
      <div
        className={cn(
          'relative z-10 mx-4 w-full max-w-md rounded-3xl border border-white/10 p-10',
          'shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_80px_rgba(124,58,237,0.05)]',
        )}
        style={{
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="mb-1">
          <BrandMark size="md" showStratiumLine accent="light" />
        </div>

        <p className="mb-6 text-[13px] tracking-wide text-[rgba(136,146,168,0.8)]">
          Executive orchestration platform
        </p>

        {error && (
          <Alert variant="error" className="mb-3 rounded-xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!requiresMfa ? (
          <>
            <form onSubmit={ssoChecked ? handleLogin : handleEmailContinue}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-email" className="text-[rgba(136,146,168,0.8)]">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => { if (email && !ssoChecked) checkSso(email); }}
                  autoFocus
                  className={FIELD_CLASS}
                />
              </div>

              {ssoChecking && (
                <div className="my-3 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-[rgba(136,146,168,0.6)]" />
                </div>
              )}

              {/* SSO button — shown when SSO is available for this domain */}
              {showSsoButton && (
                <Button
                  type="button"
                  className={cn('mt-3 h-12 w-full text-[15px] font-semibold normal-case', SSO_GRADIENT)}
                  asChild
                >
                  <a href={ssoResult?.loginUrl || '#'}>
                    {ssoResult?.displayName || `Sign in with ${ssoResult?.provider || 'SSO'}`}
                  </a>
                </Button>
              )}

              {/* Enforce SSO message */}
              {ssoResult?.ssoEnabled && ssoResult.enforceSso && (
                <p className="mt-2 text-center text-xs text-[rgba(136,146,168,0.8)]">
                  Your organisation requires SSO authentication.
                </p>
              )}

              {/* Password field */}
              {showPasswordField && (
                <>
                  {showSsoButton && <Divider label="or use password" />}

                  <div className={cn('flex flex-col gap-1.5', showSsoButton ? '' : 'mt-3')}>
                    <Label htmlFor="login-password" className="text-[rgba(136,146,168,0.8)]">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={FIELD_CLASS}
                    />
                  </div>
                  <div className="mt-1 text-right">
                    <a
                      href="/forgot-password"
                      className="text-xs text-[rgba(136,146,168,0.8)] hover:text-[#7C3AED] hover:underline"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className={cn('mt-3 h-12 w-full text-[15px] font-semibold', PRIMARY_GRADIENT)}
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign In'}
                  </Button>
                </>
              )}

              {/* Continue button — shown before SSO check */}
              {!ssoChecked && !ssoChecking && (
                <Button
                  type="submit"
                  disabled={!email}
                  className={cn('mt-3 h-12 w-full text-[15px] font-semibold', PRIMARY_GRADIENT)}
                >
                  Continue
                </Button>
              )}
            </form>

            {/* OAuth divider — only when password field is visible */}
            {showPasswordField && (
              <>
                <Divider label="or" />

                <Button asChild className={cn('mb-2', OAUTH_BUTTON)} variant="outline">
                  <a href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/microsoft`}>
                    Sign in with Microsoft 365
                  </a>
                </Button>

                <Button asChild className={OAUTH_BUTTON} variant="outline">
                  <a href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/google`}>
                    Sign in with Google
                  </a>
                </Button>

                <Button
                  variant="outline"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  className={cn('mt-2 gap-2', PASSKEY_BUTTON)}
                >
                  {passkeyLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#B794F4]" /> : <Fingerprint className="h-4 w-4" />}
                  Sign in with passkey
                </Button>

                {/* Magic link option */}
                {!showMagicLink && (
                  <Button
                    variant="ghost"
                    onClick={() => { setShowMagicLink(true); setMagicLinkEmail(email); }}
                    className="mt-2 h-10 w-full text-[13px] font-medium normal-case text-[rgba(136,146,168,0.8)] hover:bg-violet-500/[0.06] hover:text-[#7C3AED]"
                  >
                    Sign in with email link
                  </Button>
                )}

                {showMagicLink && !magicLinkSent && (
                  <div className="mt-3">
                    <Divider label="email link" />
                    <form onSubmit={handleSendMagicLink}>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="ml-email" className="text-[rgba(136,146,168,0.8)]">Email for magic link</Label>
                        <Input
                          id="ml-email"
                          type="email"
                          required
                          value={magicLinkEmail}
                          onChange={(e) => setMagicLinkEmail(e.target.value)}
                          className={FIELD_CLASS}
                        />
                      </div>
                      <Button
                        type="submit"
                        variant="outline"
                        disabled={magicLinkLoading || !magicLinkEmail}
                        className={cn('mt-3 gap-2', PASSKEY_BUTTON)}
                      >
                        {magicLinkLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#B794F4]" /> : null}
                        Send magic link
                      </Button>
                    </form>
                  </div>
                )}

                {magicLinkSent && (
                  <Alert variant="success" className="mt-3 rounded-xl">
                    <AlertDescription>
                      Check your email for a login link. It expires in 15 minutes.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </>
        ) : (
          <form onSubmit={handleMfa}>
            <p className="mb-3 text-sm text-[rgba(136,146,168,0.8)]">
              Enter the 6-digit code from your authenticator app.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mfa-code" className="text-[rgba(136,146,168,0.8)]">MFA Code</Label>
              <Input
                id="mfa-code"
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                maxLength={6}
                pattern="[0-9]{6}"
                autoFocus
                className={cn(FIELD_CLASS, 'text-center tracking-[0.3em]')}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className={cn('mt-4 h-12 w-full text-[15px] font-semibold', PRIMARY_GRADIENT)}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify'}
            </Button>
          </form>
        )}

        <div className="mt-5 text-center">
          <p className="text-xs text-[rgba(136,146,168,0.8)]">
            Don't have an account?{' '}
            <a href="/signup" className="font-semibold text-[#7C3AED] hover:underline">
              Start free trial
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
